/* eslint react/prop-types: 0 */
/* eslint prefer-destructuring: 0 */
import { useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { t } from '@lingui/macro';
import { isNumber } from 'lodash';

import { COLUMN_TYPES } from '../data-table/DataTableColumns';
import { useQueryLoader } from '../data-table/Loaders';
import { DEFAULT_INVALID_VALUE, FORMATTER_TYPES } from '../../utilities/FormattingUtilities';
import { agentFilterId, testFilterId, useTest } from '../filters';
import { LegacyIcon } from '../icon';
import ReportingDataTable from '../data-table/ReportingDataTable';
import { metricDefFormatter } from '../../utilities/MetricFormatter';
import useNg1DrillDownService from '../../services/Ng1DrillDownService';
import { Ng1DrillDownButton } from '../ng1-drill-down/Ng1DrillDownButton';

const dataSource = {
    url: '/query/table',
    params: {
        type: 'test,agent',
        entityFields: 'name,tags',
        metrics: ['status', 'availPercent', '*primary*'],
        trends: ['status'],
    },
};

const useStyles = makeStyles((theme) => ({
    tabTable: {
        paddingTop: theme.spacing(1),

        '@media print': {
            paddingTop: 0,
        },
    },
}));

const getWorstWeb = () => {
    return {
        type: COLUMN_TYPES.NUMERIC,
        title: t`Worst`,
        width: 72,
        mapping: 'maxavgResponse',
        formatter: {
            type: FORMATTER_TYPES.DURATION,
            baseUnits: 's',
            forceDecimals: true,
            maxDecimals: 3,
            ltComparison: 0.001,
        },
    };
};

const getWorstVoip = () => {
    return {
        type: COLUMN_TYPES.NUMERIC,
        title: t`Worst`,
        width: 72,
        mapper: (row) => {
            const callerMos = row.minLqmosRx;
            const calleeMos = row.minLqmosTx;
            if (!isNumber(callerMos) && !isNumber(calleeMos)) return DEFAULT_INVALID_VALUE;
            let worst;
            if (!isNumber(callerMos)) {
                worst = calleeMos;
            } else if (!isNumber(calleeMos)) {
                worst = callerMos;
            } else {
                worst = Math.min(callerMos, calleeMos);
            }
            if (!isNumber(worst)) return DEFAULT_INVALID_VALUE;

            return worst;
        },
        formatter: {
            type: FORMATTER_TYPES.DECIMAL,
            forceDecimals: true,
        },
    };
};

const isInverted = (thresholds, id) => {
    const invertedArray = thresholds
        .filter((threshold) => threshold.metricIds.includes(id))
        .map((threshold) => threshold.inverted);
    return invertedArray.length > 0 ? invertedArray[0] : false;
};

const getWorstCustom = (testType) => {
    const mainMetrics = testType.metrics.filter((metric) => metric.isMain);
    if (mainMetrics.length === 0) {
        return {
            type: COLUMN_TYPES.NUMERIC,
            title: t`Worst`,
            width: 72,
            formatter: () => DEFAULT_INVALID_VALUE,
        };
    }

    const mainMetric = mainMetrics[0];
    const inverted = isInverted(testType.thresholds, mainMetric.id);
    const metricName = `${inverted ? 'min' : 'max'}${mainMetric.name}`;
    return {
        type: COLUMN_TYPES.NUMERIC,
        title: t`Worst`,
        width: 72,
        mapping: metricName,
        formatter: metricDefFormatter(mainMetric),
    };
};

function getWorstColumn(testType) {
    if (!testType) {
        return undefined;
    }

    if (testType.name === 'Web') {
        return getWorstWeb();
    }

    if (testType.group === 'VoIP') {
        return getWorstVoip();
    }

    return getWorstCustom(testType);
}

function getColumns(test, hasNg1DrillDownPrivilege) {
    const testType = test?.typeNode;
    return [
        {
            type: COLUMN_TYPES.ICON,
            title: t`Type`,
            mapping: 'agent.typeIcon',
            sortable: true,
            width: 74,
            csvFormatter: (value, row) => row?.agent?.translatedType,
            icons: [
                {
                    getIcon: (row) => {
                        const { typeIcon } = row?.agent || {};
                        return <LegacyIcon icon={typeIcon} />;
                    },
                    getTooltip: (row) => row?.agent?.translatedType,
                },
            ],
        },
        {
            type: COLUMN_TYPES.DRILLINLINK,
            title: t`nPoint`,
            sortable: true,
            mapping: 'agent.name',
            width: 250,
            link: {
                target: '/agent-detail',
                mappedFilterParams: {
                    [agentFilterId]: 'agent',
                },
                excludedFilterParams: [testFilterId],
            },
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            title: t`Availability`,
            width: 115,
            sortable: true,
            mapping: 'availPercent',
            formatter: FORMATTER_TYPES.PERCENT,
        },
        getWorstColumn(testType),
        {
            type: COLUMN_TYPES.STATUSTREND,
            title: t`Status Trend`,
            sortable: true,
            sortProperty: 'status',
            mapping: 'trends.status.data',
        },
        {
            type: COLUMN_TYPES.TESTLOGLINK,
            isPathEnabled: test?.traceRoute?.enabled,
            mapping: 'agent',
            width: 110,
            uiOnly: true,
            link: {
                target: '/test-log',
                mappedFilterParams: {
                    [agentFilterId]: 'agent',
                },
            },
        },
        ...(hasNg1DrillDownPrivilege()
            ? [
                  {
                      type: COLUMN_TYPES.ICON,
                      mapping: 'agent',
                      width: 30,
                      uiOnly: true,
                      icons: [
                          {
                              getIcon: (row) => {
                                  return test ? <Ng1DrillDownButton agent={row?.agent} test={test} /> : null;
                              },
                              getTooltip: () => t`Launch nGeniusONE Smart Edge Service Monitor`,
                          },
                      ],
                  },
              ]
            : []),
    ];
}

const sorters = [{ property: 'status', direction: 'desc' }];

export default function TestAgents(props) {
    const test = useTest();
    const { hasNg1DrillDownPrivilege } = useNg1DrillDownService();
    const loader = useQueryLoader(dataSource);
    const columns = useMemo(() => getColumns(test, hasNg1DrillDownPrivilege), [test, hasNg1DrillDownPrivilege]);
    const classes = useStyles();

    return (
        <ReportingDataTable
            className={classes.tabTable}
            panelProps={props}
            loader={loader}
            columns={columns}
            sorters={sorters}
            deletedMapping="agent.deleted"
        />
    );
}
